const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0))
const toMm = (value, fallback = 0) => {
    const number = Number(String(value ?? '').replace(',', '.'))
    return Number.isFinite(number) ? number : fallback
}

const toOptionalMm = (value, fallback = 0) => {
    const raw = String(value ?? '').trim()
    if (!raw) return fallback
    return toMm(value, fallback)
}

export const SHOWCASE_TEXT_FONT_OPTIONS = Object.freeze([
    { value: 'Arial', label: 'Arial' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Tahoma', label: 'Tahoma' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' },
    { value: 'Courier New', label: 'Courier New' },
])

const SHOWCASE_TEXT_FONT_VALUES = new Set(SHOWCASE_TEXT_FONT_OPTIONS.map((item) => item.value))

export const normalizeShowcaseDecorationFontFamily = (value) => {
    const normalized = String(value || '').trim()
    return SHOWCASE_TEXT_FONT_VALUES.has(normalized) ? normalized : 'Arial'
}

const normalizeShowcaseDecorationDateGapMm = (value) => (
    clamp(toOptionalMm(value, 0), 0, 300)
)

export const SHOWCASE_DECORATION_TYPES = Object.freeze([
    { value: 'sharedSurname', label: 'Общая фамилия', kind: 'text', defaultWidthMm: 20, defaultHeightMm: 20, defaultFontSizeMm: 45, defaultPlacementMode: 'manual', internalOnly: true, autoSizeText: true },
    { value: 'engravingText', label: 'ФИО / основная надпись', kind: 'text', defaultWidthMm: 420, defaultHeightMm: 210, defaultFontSizeMm: 45, defaultDateFontSizeMm: 30, defaultPlacementMode: 'polished-bottom' },
    { value: 'epitaph', label: 'Эпитафия', kind: 'text', defaultWidthMm: 430, defaultHeightMm: 150, defaultFontSizeMm: 34, defaultPlacementMode: 'polished-bottom' },
    { value: 'engravedCross', label: 'Крест', kind: 'text', defaultWidthMm: 150, defaultHeightMm: 190, defaultFontSizeMm: 90, defaultText: '✝', defaultPlacementMode: 'polished-top' },
    { value: 'ceramicPortrait', label: 'Портрет на керамике', kind: 'image', defaultWidthMm: 180, defaultHeightMm: 240, shape: 'oval', defaultPlacementMode: 'polished-top' },
    { value: 'artistPortrait', label: 'Портрет — гравировка', kind: 'image', defaultWidthMm: 260, defaultHeightMm: 340, shape: 'rect', defaultPlacementMode: 'polished-top' },
    { value: 'ceramicGranite', label: 'Фотокерамика / керамогранит', kind: 'image', defaultWidthMm: 220, defaultHeightMm: 300, shape: 'rect', defaultPlacementMode: 'polished-top' },
    // Гравировка картинкой: цветы, рамка, пейзаж, виньетка… (файл-картинка или светлая плашка-заглушка).
    { value: 'engravedImage', label: 'Рисунок / гравировка', kind: 'image', defaultWidthMm: 200, defaultHeightMm: 150, shape: 'rect', defaultPlacementMode: 'manual' },
])

export const getShowcaseDecorationType = (value) => (
    SHOWCASE_DECORATION_TYPES.find((item) => item.value === String(value || ''))
    || SHOWCASE_DECORATION_TYPES[0]
)

export const normalizeShowcaseDecorationSide = (value) => (
    ['back', 'ledger'].includes(String(value || '').toLowerCase()) ? String(value).toLowerCase() : 'front'
)

export const normalizeShowcaseDecorationSlotIndex = (value) => (
    clamp(Math.round(Number(value) || 1), 1, 4)
)

export const normalizeShowcaseDecorationTextAlign = (value) => {
    const normalized = String(value || '').trim().toLowerCase()
    return ['left', 'right'].includes(normalized) ? normalized : 'center'
}

export const normalizeShowcaseDecorationShape = (value, fallback = 'rect') => {
    const shape = String(value || '').trim().toLowerCase()
    // Явный выбор «Прямоугольная» раньше терялся, если у типа по умолчанию овал.
    if (shape === 'oval' || shape === 'rect') return shape
    return String(fallback || '').trim().toLowerCase() === 'oval' ? 'oval' : 'rect'
}

/**
 * Как это будут делать в мастерской (для цены по «3D-комплекту» офиса и для бланка):
 *   надпись / эпитафия / крест — гравировка, фрезеровка или сусальное золото;
 *   жирный/рукописный шрифт — дороже за знак;
 *   крест — прямой / фигурный / ажурный;
 *   керамика — металлокерамика / фотокерамика-стекло / портретное стекло,
 *   форма (овал / прямоугольник / арка / табличка с текстом), ч/б или цвет, одинарная/двойная.
 */
const TEXT_METHOD_VALUES = new Set(['engraving', 'milling', 'gold'])
const CROSS_STYLE_VALUES = new Set(['straight', 'figured', 'openwork'])
const CERAMIC_MATERIAL_VALUES = new Set(['metal', 'photo', 'glass'])
const CERAMIC_FORM_VALUES = new Set(['oval', 'rect', 'arch', 'plaque'])

export const normalizeShowcaseDecorationWorkOptions = (source = {}, type = '') => {
    const kind = String(type || source?.type || '')
    if (kind === 'engravingText' || kind === 'epitaph' || kind === 'sharedSurname') {
        return {
            textMethod: TEXT_METHOD_VALUES.has(source?.textMethod) ? source.textMethod : 'engraving',
            fontBold: source?.fontBold === true,
        }
    }
    if (kind === 'engravedCross') {
        return {
            textMethod: TEXT_METHOD_VALUES.has(source?.textMethod) ? source.textMethod : 'engraving',
            crossStyle: CROSS_STYLE_VALUES.has(source?.crossStyle) ? source.crossStyle : 'straight',
        }
    }
    if (kind === 'ceramicPortrait') {
        return {
            ceramicMaterial: CERAMIC_MATERIAL_VALUES.has(source?.ceramicMaterial) ? source.ceramicMaterial : 'metal',
            ceramicForm: CERAMIC_FORM_VALUES.has(source?.ceramicForm) ? source.ceramicForm : '',
            colorMode: source?.colorMode === 'color' ? 'color' : 'bw',
            ceramicPersons: Number(source?.ceramicPersons) === 2 ? 2 : 1,
        }
    }
    if (kind === 'engravedImage') {
        // Какая позиция прайса («Цветы под датами», «Рамка», «Пейзаж»…) — для цены.
        return { artProductId: String(source?.artProductId || '').slice(0, 40) }
    }
    return {}
}

export const normalizeShowcaseDecorationImageMode = (value) => (
    String(value || '').trim().toLowerCase() === 'blank' ? 'blank' : 'photo'
)

export const normalizeShowcaseDecorationBlankColor = (value) => {
    const normalized = String(value || '').trim()
    return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : '#f3f4f6'
}

export const normalizeShowcaseDecorationLayoutMode = (value) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (normalized === 'triple') return 'triple'
    if (normalized === 'double') return 'double'
    return 'single'
}

export const getShowcaseDecorationLayoutSlotCount = (value) => {
    const normalized = normalizeShowcaseDecorationLayoutMode(value)
    if (normalized === 'triple') return 3
    if (normalized === 'double') return 2
    return 1
}

export const normalizeShowcaseDecorationCommonSurname = (value) => (
    String(value || '').replace(/\s+/g, ' ').trim().slice(0, 120)
)

export const normalizeShowcaseDecorationPlacementMode = (value, definition = null) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (['manual', 'polished-top', 'polished-bottom'].includes(normalized)) return normalized
    return definition?.defaultPlacementMode || 'manual'
}

const safeFaceDimensions = (dimensions = {}) => ({
    width: Math.max(1, toMm(dimensions.width, 500)),
    height: Math.max(1, toMm(dimensions.height, 1000)),
    thickness: Math.max(1, toMm(dimensions.thickness, 80)),
})

const ratio = (value, base, min = -4, max = 4) => clamp(toMm(value, 0) / Math.max(1, base), min, max)

export const getShowcaseDecorationPositionBounds = ({
    dimensions = {},
    widthMm = 0,
    heightMm = 0,
    placementMode = 'manual',
} = {}) => {
    const face = safeFaceDimensions(dimensions)
    const objectWidth = Math.max(20, toMm(widthMm, 20))
    const objectHeight = Math.max(20, toMm(heightMm, 20))
    const manual = normalizeShowcaseDecorationPlacementMode(placementMode) === 'manual'

    // В ручном режиме центр оформления разрешено доводить до самого края
    // габарита стелы. Это оставляет минимум половину портрета/текста
    // видимой и не создаёт прежний искусственный стоп за width/2, height/2.
    // Автораскладка по полировке по-прежнему держит весь объект внутри границ.
    return {
        maxX: manual
            ? face.width / 2
            : Math.max(0, (face.width - objectWidth) / 2),
        maxY: manual
            ? face.height / 2
            : Math.max(0, (face.height - objectHeight) / 2),
    }
}

const readSlotIndexFromLegacyKey = (slotKey = '') => {
    const parts = String(slotKey || '').split(':')
    if (parts.length < 3) return 1
    return normalizeShowcaseDecorationSlotIndex(parts[2])
}

const buildSlotKey = (type, side, slotIndex) => (
    `${String(type || 'engravingText')}:${normalizeShowcaseDecorationSide(side)}:${normalizeShowcaseDecorationSlotIndex(slotIndex)}`
)

export const createShowcaseDecorationTemplate = ({
    type = 'engravingText',
    side = 'front',
    slotIndex = 1,
    sourceDimensions = {},
    xMm = 0,
    yMm = 0,
    widthMm,
    heightMm,
    fontSizeMm,
    dateFontSizeMm,
    dateGapMm = 0,
    fontFamily = 'Arial',
    text = '',
    textAlign = 'center',
    assetId = '',
    fileName = '',
    rotationDeg = 0,
    shape = '',
    imageMode = 'photo',
    blankColor = '#f3f4f6',
    showDimensions = false,
    layoutMode = 'single',
    placementMode = '',
    mirrorPair = false,
    commonSurname = '',
    autoSizeText = false,
    ...workOptions
} = {}) => {
    const definition = getShowcaseDecorationType(type)
    const face = safeFaceDimensions(sourceDimensions)
    const finalWidthMm = Math.max(20, toMm(widthMm, definition.defaultWidthMm || 180))
    const finalHeightMm = Math.max(20, toMm(heightMm, definition.defaultHeightMm || 180))
    const finalFontSizeMm = Math.max(5, toMm(fontSizeMm, definition.defaultFontSizeMm || 45))
    const finalDateFontSizeMm = definition.value === 'engravingText'
        ? Math.max(5, toOptionalMm(dateFontSizeMm, Math.round(finalFontSizeMm * 0.68)))
        : finalFontSizeMm
    const finalDateGapMm = definition.value === 'engravingText'
        ? normalizeShowcaseDecorationDateGapMm(dateGapMm)
        : 0
    const finalFontFamily = normalizeShowcaseDecorationFontFamily(fontFamily)
    const normalizedSide = normalizeShowcaseDecorationSide(side)
    const normalizedSlotIndex = normalizeShowcaseDecorationSlotIndex(slotIndex)
    const normalizedLayoutMode = normalizeShowcaseDecorationLayoutMode(layoutMode)

    return {
        schemaVersion: 10,
        slotKey: buildSlotKey(definition.value, normalizedSide, normalizedSlotIndex),
        slotIndex: normalizedSlotIndex,
        type: definition.value,
        kind: definition.kind,
        label: definition.label,
        side: normalizedSide,
        targetSurface: normalizedSide === 'ledger' ? 'ledger' : 'monument',

        // Положение остаётся относительным к лицевой стороне, чтобы одна
        // ручная раскладка переносилась на камни разной H/W.
        xRatio: ratio(xMm, face.width),
        yRatio: ratio(yMm, face.height),

        // Размеры с v3 — абсолютные миллиметры. Портрет 180×240 и буквы 45 мм
        // больше не растягиваются вместе с размером конкретной стелы.
        sizeMode: 'absolute',
        widthMm: finalWidthMm,
        heightMm: finalHeightMm,
        fontSizeMm: finalFontSizeMm,
        // Для ФИО последняя строка с датами имеет независимую высоту букв.
        // Для остальных текстовых типов значение совпадает с основным размером.
        dateFontSizeMm: finalDateFontSizeMm,
        // Дополнительный вертикальный отступ между ФИО и строкой дат.
        dateGapMm: finalDateGapMm,
        // Семейство шрифта хранится в шаблоне, чтобы сцена/витрина/гостевой
        // просмотр рисовали один и тот же текст.
        fontFamily: finalFontFamily,

        // Legacy ratios оставляем как запас для старых snapshot/экспорта.
        widthRatio: clamp(finalWidthMm / face.width, 0.02, 4),
        heightRatio: clamp(finalHeightMm / face.height, 0.02, 4),
        fontHeightRatio: clamp(finalFontSizeMm / face.height, 0.005, 1),

        text: String(text || definition.defaultText || ''),
        textAlign: normalizeShowcaseDecorationTextAlign(textAlign),
        assetId: String(assetId || ''),
        fileName: String(fileName || ''),
        shape: normalizeShowcaseDecorationShape(shape, definition.shape || 'rect'),
        imageMode: definition.kind === 'image'
            ? normalizeShowcaseDecorationImageMode(imageMode)
            : 'photo',
        blankColor: normalizeShowcaseDecorationBlankColor(blankColor),
        showDimensions: showDimensions === true,
        layoutMode: normalizedLayoutMode,
        placementMode: normalizeShowcaseDecorationPlacementMode(placementMode, definition),
        // Зеркальная пара означает только симметричное положение/размер двух слотов.
        // Сам текст и изображение никогда не отражаются по X/Y.
        mirrorPair: mirrorPair === true,
        autoSizeText: definition.kind === 'text' && (autoSizeText === true || definition.autoSizeText === true),
        commonSurname: definition.value === 'engravingText' && normalizedLayoutMode !== 'single'
            ? normalizeShowcaseDecorationCommonSurname(commonSurname)
            : '',
        // Способ работы / вид керамики — для цены по товарам офиса и для бланка.
        ...normalizeShowcaseDecorationWorkOptions(workOptions, definition.value),
        ...(normalizedSide === 'ledger' && workOptions.imageSrc ? { imageSrc: String(workOptions.imageSrc), imageHasArtwork: true } : {}),
        rotationDeg: clamp(rotationDeg, -360, 360),
        updatedAt: Date.now(),
    }
}

export const materializeShowcaseDecoration = (template = {}, targetDimensions = {}) => {
    const definition = getShowcaseDecorationType(template?.type)
    const face = safeFaceDimensions(targetDimensions)
    const absoluteSize = template?.sizeMode === 'absolute' || Number(template?.schemaVersion || 0) >= 3
    const widthMm = absoluteSize
        ? Math.max(20, toMm(template?.widthMm, definition.defaultWidthMm || 180))
        : Math.max(20, Math.round(clamp(template?.widthRatio, 0.02, 4) * face.width * 10) / 10)
    const heightMm = absoluteSize
        ? Math.max(20, toMm(template?.heightMm, definition.defaultHeightMm || 180))
        : Math.max(20, Math.round(clamp(template?.heightRatio, 0.02, 4) * face.height * 10) / 10)
    const fontSizeMm = absoluteSize
        ? Math.max(5, toMm(template?.fontSizeMm, definition.defaultFontSizeMm || 45))
        : Math.max(5, Math.round(clamp(template?.fontHeightRatio, 0.005, 1) * face.height * 10) / 10)
    const dateFontSizeMm = definition.value === 'engravingText'
        ? Math.max(5, toOptionalMm(
            template?.dateFontSizeMm,
            Math.round(fontSizeMm * 0.68)
        ))
        : fontSizeMm
    const dateGapMm = definition.value === 'engravingText'
        ? normalizeShowcaseDecorationDateGapMm(template?.dateGapMm)
        : 0
    const fontFamily = normalizeShowcaseDecorationFontFamily(template?.fontFamily)
    const rawX = Math.round(clamp(template?.xRatio, -4, 4) * face.width * 10) / 10
    const rawY = Math.round(clamp(template?.yRatio, -4, 4) * face.height * 10) / 10
    const { maxX, maxY } = getShowcaseDecorationPositionBounds({
        dimensions: face,
        widthMm,
        heightMm,
        placementMode: template?.placementMode,
    })

    return {
        ...template,
        schemaVersion: Math.max(10, Number(template?.schemaVersion) || 1),
        sizeMode: absoluteSize ? 'absolute' : 'relative',
        slotIndex: normalizeShowcaseDecorationSlotIndex(
            template?.slotIndex || readSlotIndexFromLegacyKey(template?.slotKey)
        ),
        type: definition.value,
        kind: template?.kind || definition.kind,
        label: template?.label || definition.label,
        side: normalizeShowcaseDecorationSide(template?.side),
        targetSurface: template?.side === 'ledger' ? 'ledger' : 'monument',
        xMm: Math.round(clamp(rawX, -maxX, maxX) * 10) / 10,
        yMm: Math.round(clamp(rawY, -maxY, maxY) * 10) / 10,
        widthMm,
        heightMm,
        fontSizeMm,
        dateFontSizeMm,
        dateGapMm,
        fontFamily,
        textAlign: normalizeShowcaseDecorationTextAlign(template?.textAlign),
        shape: normalizeShowcaseDecorationShape(template?.shape, definition.shape || 'rect'),
        imageMode: definition.kind === 'image'
            ? normalizeShowcaseDecorationImageMode(template?.imageMode)
            : 'photo',
        blankColor: normalizeShowcaseDecorationBlankColor(template?.blankColor),
        showDimensions: template?.showDimensions === true,
        layoutMode: normalizeShowcaseDecorationLayoutMode(template?.layoutMode),
        placementMode: normalizeShowcaseDecorationPlacementMode(template?.placementMode, definition),
        mirrorPair: template?.mirrorPair === true,
        autoSizeText: definition.kind === 'text' && (template?.autoSizeText === true || definition.autoSizeText === true),
        commonSurname: definition.value === 'engravingText'
            && normalizeShowcaseDecorationLayoutMode(template?.layoutMode) !== 'single'
            ? normalizeShowcaseDecorationCommonSurname(template?.commonSurname)
            : '',
        faceDimensions: face,
    }
}

export const normalizeShowcaseDecorations = (value = []) => {
    const source = (Array.isArray(value) ? value : [])
        .filter((item) => item && item.type)
    const legacySurnameBySide = new Map()

    source.forEach((item) => {
        if (
            String(item?.type || '') !== 'engravingText'
            || normalizeShowcaseDecorationLayoutMode(item?.layoutMode) === 'single'
        ) return

        const surname = normalizeShowcaseDecorationCommonSurname(item?.commonSurname)
        if (!surname) return

        const side = normalizeShowcaseDecorationSide(item?.side)
        if (!legacySurnameBySide.has(side)) legacySurnameBySide.set(side, surname)
    })

    const normalized = source.map((item) => {
        const definition = getShowcaseDecorationType(item.type)
        const side = normalizeShowcaseDecorationSide(item.side)
        const slotIndex = normalizeShowcaseDecorationSlotIndex(
            item.slotIndex || readSlotIndexFromLegacyKey(item.slotKey)
        )
        const schemaVersion = Math.max(1, Number(item.schemaVersion) || 1)

        return {
            ...item,
            schemaVersion: Math.max(10, schemaVersion),
            slotIndex,
            slotKey: buildSlotKey(item.type, side, slotIndex),
            side,
            targetSurface: side === 'ledger' ? 'ledger' : 'monument',
            textAlign: normalizeShowcaseDecorationTextAlign(item.textAlign),
            shape: normalizeShowcaseDecorationShape(item.shape, definition.shape || 'rect'),
            imageMode: definition.kind === 'image'
                ? normalizeShowcaseDecorationImageMode(item.imageMode)
                : 'photo',
            blankColor: normalizeShowcaseDecorationBlankColor(item.blankColor),
            showDimensions: item.showDimensions === true,
            layoutMode: normalizeShowcaseDecorationLayoutMode(item.layoutMode),
            placementMode: schemaVersion >= 3
                ? normalizeShowcaseDecorationPlacementMode(item.placementMode, definition)
                : 'manual',
            mirrorPair: item?.mirrorPair === true,
            autoSizeText: definition.kind === 'text' && (
                item?.autoSizeText === true || definition.autoSizeText === true
            ),
            dateFontSizeMm: definition.value === 'engravingText'
                ? Math.max(5, toOptionalMm(
                    item?.dateFontSizeMm,
                    Math.round(Math.max(5, toMm(item?.fontSizeMm, definition.defaultFontSizeMm || 45)) * 0.68)
                ))
                : Math.max(5, toMm(item?.fontSizeMm, definition.defaultFontSizeMm || 45)),
            dateGapMm: definition.value === 'engravingText'
                ? normalizeShowcaseDecorationDateGapMm(item?.dateGapMm)
                : 0,
            fontFamily: normalizeShowcaseDecorationFontFamily(item?.fontFamily),
            // С v7 общая фамилия хранится отдельным decoration-объектом.
            // Поле на engravingText читаем только для миграции старых snapshot.
            commonSurname: '',
        }
    })

    legacySurnameBySide.forEach((surname, side) => {
        const alreadyExists = normalized.some((item) => (
            item.type === 'sharedSurname' && item.side === side
        ))
        if (alreadyExists) return

        const pair = normalized.filter((item) => (
            item.type === 'engravingText'
            && item.side === side
            && item.layoutMode !== 'single'
        ))
        if (!pair.length) return

        const fontSizeMm = Math.max(12, ...pair.map((item) => Number(item.fontSizeMm || 45)))
        const xRatio = pair.reduce((sum, item) => sum + Number(item.xRatio || 0), 0) / pair.length
        const topRatio = Math.max(...pair.map((item) => (
            Number(item.yRatio || 0) + Math.max(0.01, Number(item.heightRatio || 0.12)) / 2
        )))
        const surnameYRatio = clamp(topRatio + Math.max(0.035, Number(pair[0]?.fontHeightRatio || 0.045) * 1.15), -4, 4)

        normalized.push({
            schemaVersion: 10,
            slotKey: buildSlotKey('sharedSurname', side, 1),
            slotIndex: 1,
            type: 'sharedSurname',
            kind: 'text',
            label: 'Общая фамилия',
            side,
            xRatio: clamp(xRatio, -4, 4),
            yRatio: surnameYRatio,
            sizeMode: 'absolute',
            widthMm: 20,
            heightMm: 20,
            fontSizeMm,
            dateFontSizeMm: fontSizeMm,
            dateGapMm: 0,
            fontFamily: normalizeShowcaseDecorationFontFamily(pair[0]?.fontFamily),
            widthRatio: 0.02,
            heightRatio: 0.02,
            fontHeightRatio: Math.max(0.005, Number(pair[0]?.fontHeightRatio || 0.045)),
            text: surname,
            textAlign: 'center',
            assetId: '',
            fileName: '',
            shape: 'rect',
            imageMode: 'photo',
            blankColor: '#f3f4f6',
            showDimensions: false,
            layoutMode: pair.some((item) => item.layoutMode === 'triple') ? 'triple' : 'double',
            placementMode: 'manual',
            mirrorPair: false,
            autoSizeText: true,
            commonSurname: '',
            rotationDeg: 0,
            updatedAt: Date.now(),
        })
    })

    return normalized
}

export const upsertShowcaseDecoration = (decorations = [], template = {}) => {
    const source = normalizeShowcaseDecorations(decorations)
    const normalized = normalizeShowcaseDecorations([template])[0]
    if (!normalized) return source
    const index = source.findIndex((item) => item.slotKey === normalized.slotKey)
    if (index < 0) return [...source, normalized]

    const next = [...source]
    next[index] = normalized
    return next
}

export const upsertShowcaseDecorations = (decorations = [], templates = []) => (
    (Array.isArray(templates) ? templates : []).reduce(
        (result, template) => upsertShowcaseDecoration(result, template),
        normalizeShowcaseDecorations(decorations)
    )
)

export const removeShowcaseDecorations = (decorations = [], {
    type = '',
    side = '',
    slotIndex = 0,
    all = false,
} = {}) => {
    if (all) return []
    const normalizedSide = side ? normalizeShowcaseDecorationSide(side) : ''
    const normalizedSlotIndex = slotIndex ? normalizeShowcaseDecorationSlotIndex(slotIndex) : 0

    return normalizeShowcaseDecorations(decorations).filter((item) => {
        if (type && item.type !== type) return true
        if (normalizedSide && item.side !== normalizedSide) return true
        if (normalizedSlotIndex && item.slotIndex !== normalizedSlotIndex) return true
        return false
    })
}
